import "../../assets/scss/addressManager.scss";

import { debounce } from "lodash";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import StateSelect, { USState } from "@components/elements/StateSelect";
import AutocompleteInput from "@elements/AutocompleteInput";
import { yupResolver } from "@hookform/resolvers/yup";
import useAddressSuggestions, { AddressSuggestion } from "@hooks/user/useAddressSuggestions";
import Checkbox from "@material-ui/core/Checkbox";
import {
  Button,
  Feedback,
  FormGroupMessage,
  Label,
  Text,
  TextField,
  Title,
} from "@runwayhealth/runway-components-react";
import { Address } from "@types-runway/user";
import states from "@utils/forms/USstates.json";

import { AddressFormCard, AddressFormGroup, ConfirmButton } from "../ShippingAddress";
import { addressInitializerObject, addressSchema } from "./formSchema";

type AddressFormProps = {
  closeForm: () => void;
  onSubmit: (address: Address) => void;
  address?: Address;
  enableCardDecor?: boolean;
  restrictPOBox?: boolean;
  pendingAddressConfirmation?: boolean;
};

const AddressForm = ({
  closeForm,
  onSubmit,
  address,
  enableCardDecor,
  restrictPOBox,
  pendingAddressConfirmation,
}: AddressFormProps) => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const { getSuggestionResults } = useAddressSuggestions();
  const isMountedRef = useRef(true);

  // The debounced function must own the state update. A lodash-debounced
  // function returns the *previous* invocation's result, so awaiting it
  // showed each query's suggestions one keystroke late (or never).
  const [debouncedSearch] = useState(() =>
    debounce((query: string) => {
      getSuggestionResults(query).then((results) => {
        if (isMountedRef.current) {
          setSuggestions(results);
        }
      });
    }, 300)
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<Address>({
    mode: "all",
    resolver: yupResolver(addressSchema(restrictPOBox ?? false)),
    defaultValues: addressInitializerObject,
  });

  const lookupAddress = (query: string) => {
    clearErrors("street");
    setValue("street", query);

    if (query === "") {
      debouncedSearch.cancel();
      setSuggestions([]);
      return;
    }
    debouncedSearch(query);
  };

  const handleAddressSelection = (chosenAddress: AddressSuggestion) => {
    // Only update if the street line is defined.
    if (chosenAddress.street_line) {
      clearErrors();
      setSuggestions([]);
      reset({
        street: chosenAddress.street_line,
        city: chosenAddress.city,
        state: chosenAddress.state,
        postalCode: chosenAddress.zipcode,
      });
    }
  };

  useEffect(() => {
    if (address) {
      reset(address);
      setValue("default", address.default);
    }
  }, [address, reset, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AddressFormCard $enableCardDecor={enableCardDecor}>
        <Title>Enter your shipping address</Title>
        {/* Street Line 1 */}
        <AddressFormGroup>
          <Label>Address 1</Label>
          <Controller
            control={control}
            name="street"
            render={() => (
              <AutocompleteInput
                open={suggestions.length > 0}
                options={suggestions}
                // Suggestions are already a server-side search result; MUI's
                // default substring filter would hide normalized matches.
                filterOptions={(options) => options}
                onChange={lookupAddress}
                capturedVariant={(chosenAddress) => {
                  handleAddressSelection(chosenAddress as AddressSuggestion);
                }}
                // Must be stable while typing: a per-keystroke value here
                // re-seeded the input mid-typing and dropped characters.
                defaultValue={address?.street}
              />
            )}
          />
          <FormGroupMessage>
            {errors.street && (
              <Feedback size="sm" isInvalid>
                {errors.street?.message}
              </Feedback>
            )}
          </FormGroupMessage>
        </AddressFormGroup>
        {/* Address 2 */}
        <AddressFormGroup>
          <Label>Address 2</Label>
          <TextField
            data-testid="address-secondary"
            id="secondary"
            isInvalid={errors.secondary}
            {...register("secondary")}
          ></TextField>
          <FormGroupMessage>
            {errors.secondary && (
              <Feedback size="sm" isInvalid>
                {errors.secondary?.message}
              </Feedback>
            )}
          </FormGroupMessage>
        </AddressFormGroup>
        {/* City */}
        <AddressFormGroup>
          <Label>City</Label>
          <TextField
            id="city"
            data-testid="address-city"
            isInvalid={errors.city}
            {...register("city")}
          ></TextField>
          <FormGroupMessage>
            {errors.city && (
              <Feedback size="sm" isInvalid>
                {errors.city?.message}
              </Feedback>
            )}
          </FormGroupMessage>
        </AddressFormGroup>
        <div className="state-zip-group">
          <AddressFormGroup className="state-select">
            <Label className="state-label">State</Label>
            <Controller
              control={control}
              name="state"
              render={({ field: { name, onBlur } }) => (
                <StateSelect
                  name={name}
                  value={getValues("state")}
                  options={states as USState[]}
                  onBlur={onBlur}
                  onChange={(stateAbbrv: string) => {
                    setValue("state", stateAbbrv);
                    clearErrors("state");
                  }}
                />
              )}
            />
            <FormGroupMessage className="rw-checkbox-feedback">
              {errors.state && (
                <Feedback size="sm" isInvalid>
                  {errors.state?.message}
                </Feedback>
              )}
            </FormGroupMessage>
          </AddressFormGroup>
          <AddressFormGroup>
            <Label>Zip Code</Label>
            <TextField
              id="postalCode"
              data-testid="address-zipcode"
              isInvalid={errors.postalCode}
              {...register("postalCode")}
            ></TextField>
            <FormGroupMessage>
              {errors.postalCode && (
                <Feedback size="sm" isInvalid>
                  {errors.postalCode?.message}
                </Feedback>
              )}
            </FormGroupMessage>
          </AddressFormGroup>
        </div>
        <AddressFormGroup>
          <Controller
            control={control}
            name="default"
            render={({ field }) => (
              <>
                <Checkbox
                  data-testid="address-default"
                  color="default"
                  checked={field.value}
                  onChange={field.onChange}
                />
                <Text as="span" size="sm" noMarginBottom>
                  Set as default shipping address
                </Text>
              </>
            )}
          />
        </AddressFormGroup>
        <div className="payment-form-button-wrapper">
          <Button size="lg" variant="outline" onClick={closeForm}>
            Cancel
          </Button>
          <ConfirmButton
            data-testid="address-save-button"
            type="submit"
            size="lg"
            $pendingConfirmation={pendingAddressConfirmation}
          >
            Save
          </ConfirmButton>
        </div>
      </AddressFormCard>
    </form>
  );
};

export default AddressForm;
